import { notify } from '@affine/component';
import { useActiveBlocksuiteEditor } from '@affine/core/components/hooks/use-block-suite-editor';
import { DocsService } from '@affine/core/modules/doc';
import { TagService } from '@affine/core/modules/tag';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { DocModeProvider } from '@blocksuite/affine/shared/services';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { extractCitationTokens } from './citation-tokens';
import { TypePickerPopover } from './components/type-picker-popover';
import {
  type AddCardResult,
  addCardToCanvas,
} from './handlers/add-to-canvas';
import {
  addToNotes,
  type AddToNotesResult,
} from './handlers/add-to-notes';
import { NODE_TYPE_REGISTRY } from './handlers/canvas-helpers';
import {
  createBranchFromSelection,
  type CreateBranchResult,
} from './handlers/create-branch';
import {
  CURIESCIOUS_EVENTS,
  type CuriesciousActionDetail,
  type CuriesciousEventName,
  MINDMAP_CHANGED_EVENT,
  type MindmapNodeType,
} from './types';

/**
 * Window-event shorthand. The outline panel listens for this so the
 * card list refreshes immediately after add / undo without waiting for
 * the polling tick. Kept under the old "mindmap-changed" name during
 * the canvas pivot for backward compat — the canvas IS the new
 * mindmap conceptually.
 */
const fireCanvasChanged = () => {
  window.dispatchEvent(new CustomEvent(MINDMAP_CHANGED_EVENT));
};

interface PendingAdd {
  detail: CuriesciousActionDetail;
  anchor: { x: number; y: number; width: number; height: number };
}

const FALLBACK_RECT = { x: 240, y: 240, width: 0, height: 0 };

/**
 * Selection Router
 *
 * Single app-level listener for the custom events dispatched by
 * `curiescious-toolbar.ts` when the user highlights text in the
 * editor and picks an action.
 *
 * Card-on-canvas pivot: when add-to-mindmap fires (event name kept for
 * backward compat; the user-facing label is now "Add to Canvas"), the
 * router shows the typed-node picker popover anchored to the
 * selection bbox. The picked type drives the background color of a
 * new `affine:note` (EdgelessOnly) card on the doc's surface. The
 * toast offers Undo + Open Canvas; Open Canvas switches the editor
 * to edgeless so the user sees what they're building.
 */
export function SelectionRouter() {
  const [activeEditor] = useActiveBlocksuiteEditor();
  const editorRef = useRef(activeEditor);
  useEffect(() => {
    editorRef.current = activeEditor;
  }, [activeEditor]);

  // Services needed for the create-branch handler (new doc creation,
  // tag application, split-pane navigation). Stable refs across
  // renders so the window-level event handler can read them without
  // re-attaching.
  const workspaceService = useService(WorkspaceService);
  const docsService = useService(DocsService);
  const tagService = useService(TagService);
  const workbenchService = useService(WorkbenchService);
  const servicesRef = useRef({
    workspace: workspaceService,
    docsService,
    tagService,
    workbench: workbenchService,
  });
  useEffect(() => {
    servicesRef.current = {
      workspace: workspaceService,
      docsService,
      tagService,
      workbench: workbenchService,
    };
  }, [workspaceService, docsService, tagService, workbenchService]);

  const [pending, setPending] = useState<PendingAdd | null>(null);

  const openCanvas = useCallback(() => {
    const editor = editorRef.current;
    const host = editor?.host;
    if (!host) return;
    try {
      host.std.get(DocModeProvider).setEditorMode('edgeless');
    } catch {
      /* mode service unavailable — silent */
    }
  }, []);

  const runAdd = useCallback(
    async (detail: CuriesciousActionDetail, nodeType: MindmapNodeType) => {
      try {
        const result = await addCardToCanvas(
          editorRef.current,
          detail,
          nodeType
        );
        fireCanvasChanged();
        showResultToast(result, openCanvas);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to add the selection to the canvas.';
        notify.error({ title: 'Add to canvas failed', message });
      }
    },
    [openCanvas]
  );

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<Partial<CuriesciousActionDetail>>;
      const name = event.type as CuriesciousEventName;
      const text = custom.detail?.text ?? '';
      if (!text) return;

      const citationTokens =
        custom.detail?.citationTokens ?? extractCitationTokens(text);
      const detail: CuriesciousActionDetail = {
        text,
        citationTokens,
        sourceDocId: custom.detail?.sourceDocId,
        sourceBlockId: custom.detail?.sourceBlockId,
        sourceRect: custom.detail?.sourceRect,
      };

      switch (name) {
        case CURIESCIOUS_EVENTS.sendToChat:
          // Superseded by Ask Curiescious — deliberately a no-op so a
          // stale BlockSuite cache that still dispatches this event
          // doesn't crash anything.
          break;
        case CURIESCIOUS_EVENTS.addToMindmap: {
          // Event name kept for backward compat; the action is now
          // "Add to Canvas" — see the toolbar label.
          const anchor = detail.sourceRect ?? FALLBACK_RECT;
          setPending({ detail, anchor });
          break;
        }
        case CURIESCIOUS_EVENTS.addToNotes: {
          const services = servicesRef.current;
          addToNotes({
            workspace: services.workspace,
            docsService: services.docsService,
            tagService: services.tagService,
            workbench: services.workbench,
            editor: editorRef.current,
            detail,
          })
            .then(result => {
              showNotesToast(result);
            })
            .catch(err => {
              const message =
                err instanceof Error
                  ? err.message
                  : 'Failed to add the selection to notes.';
              notify.error({ title: 'Add to notes failed', message });
            });
          break;
        }
        case CURIESCIOUS_EVENTS.createBranch: {
          const services = servicesRef.current;
          createBranchFromSelection({
            workspace: services.workspace,
            docsService: services.docsService,
            tagService: services.tagService,
            workbench: services.workbench,
            editor: editorRef.current,
            detail,
          })
            .then(result => {
              fireCanvasChanged();
              showBranchToast(result);
            })
            .catch(err => {
              const message =
                err instanceof Error
                  ? err.message
                  : 'Failed to create a branch from this selection.';
              notify.error({ title: 'Create branch failed', message });
            });
          break;
        }
      }
    };

    const names = Object.values(CURIESCIOUS_EVENTS);
    for (const name of names) window.addEventListener(name, handler);
    return () => {
      for (const name of names) window.removeEventListener(name, handler);
    };
  }, []);

  if (!pending) return null;

  return (
    <TypePickerPopover
      anchor={pending.anchor}
      onPick={nodeType => {
        const { detail } = pending;
        setPending(null);
        runAdd(detail, nodeType).catch(err => {
          console.warn('[selection-router] add-to-canvas failed:', err);
        });
      }}
      onDismiss={() => setPending(null)}
    />
  );
}

/**
 * Toast wording mirrors the typed-node terminology so the user sees a
 * confirmation in the same language as the picker. Primary action
 * switches to edgeless; footer hosts a quiet Undo link so accidental
 * adds are one click to reverse.
 */
function showResultToast(
  result: AddCardResult,
  openCanvas: () => void
): void {
  const meta = NODE_TYPE_REGISTRY[result.nodeType];
  notify.success({
    title: `Added as ${meta.label}`,
    message: 'Switch to edgeless view to see and arrange your cards.',
    action: {
      label: 'Open Canvas',
      onClick: openCanvas,
    },
    footer: (
      <button
        type="button"
        onClick={() => {
          try {
            result.undo();
            fireCanvasChanged();
          } catch {
            /* already undone */
          }
        }}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '4px 0 0',
          color: 'rgba(0, 43, 87, 0.65)',
          cursor: 'pointer',
          fontSize: '12px',
          textDecoration: 'underline',
        }}
      >
        Undo add
      </button>
    ),
  });
}

/**
 * Toast for a successful Create Branch. Primary action re-focuses
 * the branch pane in case the user clicked away; footer offers a
 * quiet Undo that deletes the freshly-created branch doc + clears
 * the source-side marker.
 */
/**
 * Toast for a successful Add to Notes. Primary action opens the Notes
 * doc beside the source; footer offers a quiet Undo that removes the
 * appended entry (and deletes the Notes doc if THIS call created it).
 */
function showNotesToast(result: AddToNotesResult): void {
  notify.success({
    title: result.notesDocCreatedNow
      ? 'Notes created — entry saved'
      : 'Added to Notes',
    message: result.notesDocCreatedNow
      ? 'A new Research Notes doc is in your sidebar.'
      : 'Latest entry at the top of your Research Notes.',
    action: {
      label: 'Open Notes',
      onClick: () => {
        result.openBeside();
      },
    },
    footer: (
      <button
        type="button"
        onClick={() => {
          try {
            result.undo();
          } catch {
            /* already undone */
          }
        }}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '4px 0 0',
          color: 'rgba(0, 43, 87, 0.65)',
          cursor: 'pointer',
          fontSize: '12px',
          textDecoration: 'underline',
        }}
      >
        Undo
      </button>
    ),
  });
}

function showBranchToast(result: CreateBranchResult): void {
  notify.success({
    title: 'Branch created',
    message: result.branchTitle,
    action: {
      label: 'Open',
      onClick: () => {
        result.openBeside();
      },
    },
    footer: (
      <button
        type="button"
        onClick={() => {
          try {
            result.undo();
            fireCanvasChanged();
          } catch {
            /* already undone */
          }
        }}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '4px 0 0',
          color: 'rgba(0, 43, 87, 0.65)',
          cursor: 'pointer',
          fontSize: '12px',
          textDecoration: 'underline',
        }}
      >
        Undo branch
      </button>
    ),
  });
}
